//Local components
import ContainedChip from "../Components/basics/ContainedChip";

//Material Icons
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { Cancel, CheckCircle, Error, Pending } from "@mui/icons-material";
import PendingIcon from "@mui/icons-material/Pending";

//UI Material
import { CircularProgress, Stack } from "@mui/material";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForText,
} from "./typography";
import { selectDateString } from "./date";

//Function to check the order status
export const checkOrderStatus = (productsStatus, canceledBy = "") => {
  if (canceledBy !== "") {
    return "Cancelada";
  } else {
    if (productsStatus.every((product) => product.status === "PENDING")) {
      return "En espera";
    } else if (
      productsStatus.some((product) => product.status === "IN_PROGRESS")
    ) {
      return "En progreso";
    } else if (
      productsStatus.every((product) => product.status === "FINISHED")
    ) {
      return "Finalizada";
    } else if (
      productsStatus.some((product) => product.status === "FINISHED")
    ) {
      return "En progreso";
    } else {
      return "Estado desconocido";
    }
  }
};

export const checkOrderStatusFromProducts = (products, canceledBy = "") => {
  if (canceledBy !== "") {
    return {
      status: "Cancelada",
      icon: <Cancel sx={{ color: "white" }} />,
      color: "#d32f2f",
    };
  } else {
    if (products.every((product) => product.status === "PENDING")) {
      return {
        status: "En espera",
        icon: <Pending sx={{ color: "white" }} />,
        color: "#ed6c02",
      };
    } else if (products.some((product) => product.status === "IN_PROGRESS")) {
      return {
        status: "En progreso",
        icon: <CircularProgress size={13} sx={{ color: "white" }} />,
        color: "#0288d1",
      };
    } else if (products.every((product) => product.status === "FINISHED")) {
      return {
        status: "Finalizada",
        icon: <CheckCircle sx={{ color: "white" }} />,
        color: "#2e7d32",
      };
    } else if (products.some((product) => product.status === "FINISHED")) {
      return {
        status: "En progreso",
        icon: <CircularProgress size={13} sx={{ color: "white" }} />,
        color: "#0288d1",
      };
    } else {
      return {
        status: "Estado desconocido",
        icon: <Error sx={{ color: "white" }} />,
        color: "#d32f2f",
      };
    }
  }
};

export const checkProductStatus = (status) => {
  switch (status.toLowerCase()) {
    case "pending":
      return {
        color: "#ed6c02",
        icon: <Pending sx={{ color: "white" }} />,
        status: "en espera",
      };
    case "in_progress":
      return {
        color: "#0288d1",
        icon: <CircularProgress size={13} sx={{ color: "white" }} />,
        status: "en progreso",
      };
    case "finished":
      return {
        color: "#2e7d32",
        icon: <CheckCircle sx={{ color: "white" }} />,
        status: "finalizado",
      };
    default:
      return {
        color: "#d32f2f",
        icon: <Error sx={{ color: "white" }} />,
        status: "estado desconocido",
      };
  }
};

//Function to generate order status chip
export const generateChipForOrderStatus = (status) => {
  switch (status.toLowerCase()) {
    case "en espera":
      return (
        <ContainedChip
          sx={{ width: "350px" }}
          text="En espera"
          icon={<PendingIcon fontSize="small" />}
          color="warning"
        />
      );
    case "en progreso":
      return (
        <ContainedChip
          sx={{ width: "350px" }}
          text="En progreso"
          icon={<CircularProgress size={13} />}
          color="info"
        />
      );
    case "finalizada":
      return (
        <ContainedChip
          sx={{ width: "350px" }}
          text="Finalizada"
          icon={<CheckCircle fontSize="small" />}
          color="success"
        />
      );
    case "cancelada":
      return (
        <ContainedChip
          sx={{ width: "350px" }}
          text="Cancelada"
          icon={<Cancel fontSize="small" />}
          color="error"
        />
      );
    default:
      return (
        <ContainedChip
          sx={{ width: "350px" }}
          text="Pendiente"
          icon={<HourglassBottomIcon fontSize="small" />}
        />
      );
  }
};

//Function to get filtered orders by date range
export const getFilteredTOrdersByDateRangeFromLocalTimeStrings = (
  orders,
  min,
  max,
  key = "dueDate"
) => {
  const convertDateToUTCString = (validDateString) => {
    try {
      let date = new Date(validDateString);
      return new Date(date).toISOString();
    } catch (e) {
      return null;
    }
  };

  return orders.filter(
    (order) =>
      convertDateToUTCString(max) > order[key] &&
      convertDateToUTCString(min) < order[key]
  );
};

//Function to generate the status name in spanish
export const generateStatusName = (status) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "En espera";
    case "in_progress":
      return "En progreso";
    case "finished":
      return "Finalizada";
    case "canceled":
      return "Cancelada";
    default:
      return "Sin información";
  }
};

//Function to filter orders data according to the status
export const filterOrdersByStatus = (index, orders) => {
  const status = [
    "Todas",
    "Pendientes",
    "En espera",
    "En progreso",
    "Finalizada",
    "Cancelada",
    "Nuevas",
  ];

  if (index === 0) {
    return orders;
  } else if (index === 1) {
    return orders.filter(
      (order) =>
        checkOrderStatus(order.productsStatus, order.canceledBy) ===
          "En espera" ||
        checkOrderStatus(order.productsStatus, order.canceledBy) ===
          "En progreso"
    );
  } else if (index === 6) {
    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      const currentDate = new Date();

      return (
        orderDate.getDate() === currentDate.getDate() &&
        orderDate.getMonth() === currentDate.getMonth() &&
        orderDate.getFullYear() === currentDate.getFullYear()
      );
    });
  } else {
    return orders.filter(
      (order) =>
        checkOrderStatus(order.productsStatus, order.canceledBy) ===
        status[index]
    );
  }
};

//Function to generate finished orders quantity
export const generateFinishedOrdersQuantity = (finishedOrders) => {
  return finishedOrders.reduce(function (total, currVal) {
    return total + currVal.quantity;
  }, 0);
};

//Function to check if order is pending delayed
export const checkIfPendingOrderIsDelayed = (order) => {
  if (
    checkOrderStatus(order.productsStatus, order.canceledBy) ===
      "En progreso" ||
    checkOrderStatus(order.productsStatus, order.canceledBy) === "En espera"
  ) {
    if (order.dueDate < new Date().toISOString()) {
      return (
        <Stack>
          {typographyForText(
            new Date(order.dueDate).toLocaleDateString(),
            order.priority && "bold",
            order.priority && "white"
          )}
          {typographyForCustomizedFontSize(
            selectDateString(order.dueDate),
            "12px",
            "RED"
          )}
        </Stack>
      );
    } else {
      return typographyForText(
        new Date(order.dueDate).toLocaleDateString(),
        order.priority && "bold",
        order.priority && "white"
      );
    }
  } else {
    return typographyForText(new Date(order.dueDate).toLocaleDateString());
  }
};
